/**
 * Shared Hook for Embed Widget Editor
 * Contains all the logic for embed configuration, code generation, and security helpers.
 * Used by both /distribute (Tailwind) and EmbedWidgetEditorPage (inline styles).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

import { debugLog } from '../debug';
import {
  type AdvancedWidgetConfig,
  type Brain,
  DEFAULT_ADVANCED_CONFIG,
  DEFAULT_CONFIG,
  type EmbedConfig,
  generateEmbedCode,
  type SelectedBrain,
} from '../widgetConfig';

export * from '../widgetConfig';

export interface UseEmbedEditorOptions {
  /** User's handle */
  userHandle: string;
  /** User's ID */
  userId: string;
  /** Base URL for the app (e.g., https://spaces.bldr.it) */
  appBaseUrl: string;
  /** API base URL for backend calls (e.g., https://api.bldr.it) */
  apiBaseUrl?: string;
  /** Media base URL for images */
  mediaBaseUrl?: string;
  /** Asset API URL for WebSocket connections */
  assetApiUrl?: string;
  /** Function to fetch user's brains */
  fetchBrains: () => Promise<Brain[]>;
  /** Optional brain to pre-select (e.g. when the editor is opened from a specific brain's actions) */
  initialBrain?: SelectedBrain;
}

export interface UseEmbedEditorResult {
  // State
  config: EmbedConfig;
  setConfig: React.Dispatch<React.SetStateAction<EmbedConfig>>;
  brains: Brain[];
  loading: boolean;
  copied: boolean;

  // Computed values
  embedPath: string;
  embedCode: string;

  // Actions
  updateConfig: (field: keyof EmbedConfig, value: string | boolean) => void;
  updateAdvancedConfig: <K extends keyof AdvancedWidgetConfig>(
    section: K,
    updates: Partial<AdvancedWidgetConfig[K]>,
  ) => void;
  resetAdvancedConfig: () => void;
  handleCopy: () => void;

  // Modal state
  showBrainPicker: boolean;
  setShowBrainPicker: React.Dispatch<React.SetStateAction<boolean>>;
  showThemePicker: boolean;
  setShowThemePicker: React.Dispatch<React.SetStateAction<boolean>>;
}

// ============================================================================
// Main Hook
// ============================================================================

export function useEmbedEditor(options: UseEmbedEditorOptions): UseEmbedEditorResult {
  const {
    userHandle,
    userId,
    appBaseUrl,
    apiBaseUrl = '',
    mediaBaseUrl = '',
    assetApiUrl = '',
    fetchBrains,
    initialBrain,
  } = options;

  // State
  const [config, setConfig] = useState<EmbedConfig>(() =>
    initialBrain
      ? {
          ...DEFAULT_CONFIG,
          type: 'brain',
          brainId: initialBrain.id,
          brainName: initialBrain.name,
          selectedBrains: [initialBrain],
        }
      : { ...DEFAULT_CONFIG },
  );
  const [brains, setBrains] = useState<Brain[]>([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  // Modal state
  const [showBrainPicker, setShowBrainPicker] = useState(false);
  const [showThemePicker, setShowThemePicker] = useState(false);

  // Set profile handle when user handle is available
  useEffect(() => {
    if (userHandle && !config.profileHandle) {
      setConfig((prev) => ({ ...prev, profileHandle: userHandle }));
    }
  }, [userHandle, config.profileHandle]);

  // Fetch brains and PoVs
  useEffect(() => {
    // If no userId yet, set loading to false and wait
    if (!userId) {
      setLoading(false);
      return;
    }

    const loadData = async () => {
      setLoading(true);
      try {
        debugLog('[useEmbedEditor] Fetching brains for userId:', userId);
        const fetchedBrains = await fetchBrains();
        debugLog('[useEmbedEditor] Fetched brains:', fetchedBrains.length);

        // Update brains state (even if empty, to reflect actual data)
        setBrains(fetchedBrains);

        // Auto-select defaults for multi-select if nothing is selected yet
        setConfig((prev) => {
          const updates: Partial<EmbedConfig> = {};

          // Legacy single-select default (for backward compatibility)
          if (!prev.brainId && fetchedBrains.length > 0) {
            updates.brainId = fetchedBrains[0]._id;
          }

          // Multi-select default - auto-select first brain if none selected
          if ((!prev.selectedBrains || prev.selectedBrains.length === 0) && fetchedBrains.length > 0) {
            updates.selectedBrains = [
              {
                id: fetchedBrains[0]._id,
                name: fetchedBrains[0].name,
                displayImage: fetchedBrains[0].displayImage,
              },
            ];
          }

          if (Object.keys(updates).length > 0) {
            return { ...prev, ...updates };
          }
          return prev;
        });
      } catch (error) {
        console.error('[useEmbedEditor] Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [userId, fetchBrains]);

  // Generate embed path based on config
  const embedPath = useMemo(() => {
    if (config.type === 'brain') {
      return config.brainId ? `/brain/${config.brainId}` : '';
    }
    return config.profileHandle ? `/p/${config.profileHandle}` : '';
  }, [config.type, config.brainId, config.profileHandle]);

  // Generate embed code
  const embedCode = useMemo(() => {
    return generateEmbedCode(
      config,
      embedPath,
      userId,
      userHandle,
      appBaseUrl,
      apiBaseUrl,
      mediaBaseUrl,
      assetApiUrl,
    );
  }, [config, embedPath, userId, userHandle, appBaseUrl, apiBaseUrl, mediaBaseUrl, assetApiUrl]);

  // Update config field
  const updateConfig = useCallback((field: keyof EmbedConfig, value: string | boolean) => {
    setConfig((prev) => ({ ...prev, [field]: value }));
  }, []);

  // Update advanced config section
  const updateAdvancedConfig = useCallback(
    <K extends keyof AdvancedWidgetConfig>(section: K, updates: Partial<AdvancedWidgetConfig[K]>) => {
      setConfig((prev) => ({
        ...prev,
        advanced: {
          ...prev.advanced,
          [section]: {
            ...prev.advanced[section],
            ...updates,
          },
        },
      }));
    },
    [],
  );

  // Reset advanced config to defaults
  const resetAdvancedConfig = useCallback(() => {
    setConfig((prev) => ({
      ...prev,
      advanced: DEFAULT_ADVANCED_CONFIG,
    }));
  }, []);

  // Copy to clipboard
  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(embedCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [embedCode]);

  return {
    // State
    config,
    setConfig,
    brains,
    loading,
    copied,

    // Computed values
    embedPath,
    embedCode,

    // Actions
    updateConfig,
    updateAdvancedConfig,
    resetAdvancedConfig,
    handleCopy,

    // Modal state
    showBrainPicker,
    setShowBrainPicker,
    showThemePicker,
    setShowThemePicker,
  };
}

export default useEmbedEditor;
